import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useCallApi } from '../../../hooks/useCallApi';
import { useConfirmationBeforeAction } from '../../../hooks/useConfirmationBeforeAction';
import { useBatchId } from '../../../hooks/useBatchId';
import { usePreworkGroupId } from '../../../hooks/usePreworkGroupId';
import { PREWORK_EDIT_GROUP, PREWORKGROUPS } from '../../../shared/Routes';
import { Modal } from 'antd';
import { updatePreworkGroup } from '../../../service/PreWorkService';
import { PreworkGroup } from '../../../shared/PreWorkServiceTypes';
import { CandidatesBatchState } from '../CreateGroupPage/CandidatesList.store';
import { useSnapshot } from 'valtio';

export const useEditGroupPreWork = () => {
	const { t } = useTranslation();
	const { srcmId } = useAuth();
	const { callApi } = useCallApi();
	const { confirmBeforeAction } = useConfirmationBeforeAction();
	const { navigateToRoute } = useBatchId();
	const { preworkGroupId, fetchGroup, navigateWithPreworkGroupId } =
		usePreworkGroupId();
	const [group, setGroup] = useState<PreworkGroup>();
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);

	const onConfirmUpdateGroup = useCallback(
		async (candidateIds: Array<number>, addMoreCandidates?: boolean) => {
			try {
				await callApi(
					() =>
						updatePreworkGroup({
							functionarySrcmId: srcmId as string,
							preworkGroupId,
							candidateIds,
							batchId: candidateBatchSnap.selectedBatch?.id,
						}),
					'errorOccurredWhileUpdatingGroup'
				);
				Modal.success({
					content: t('updatedGroupSuccessfully'),
					afterClose: () => {
						if (addMoreCandidates) {
							navigateWithPreworkGroupId(PREWORK_EDIT_GROUP, preworkGroupId);
						} else {
							navigateToRoute(PREWORKGROUPS);
						}
					},
				});
			} catch (e) {
				// Do Nothing
			}
		},
		[
			callApi,
			srcmId,
			t,
			preworkGroupId,
			navigateToRoute,
			navigateWithPreworkGroupId,
			candidateBatchSnap.selectedBatch?.id,
		]
	);

	const updateGroup = useCallback(
		(
			candidateIds: Array<number>,
			ConfirmationMessage: string,
			addMoreCandidate?: boolean
		) => {
			confirmBeforeAction('confirmUpdateGroup', ConfirmationMessage, () =>
				onConfirmUpdateGroup(candidateIds, addMoreCandidate)
			);
		},
		[confirmBeforeAction, onConfirmUpdateGroup]
	);

	const loadGroup = useCallback(async () => {
		try {
			const fetchedGroup = await fetchGroup();
			setGroup(fetchedGroup);
		} catch (e) {
			//
		}
	}, [fetchGroup]);

	useEffect(() => {
		if (preworkGroupId) {
			void loadGroup();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preworkGroupId]);

	return { updateGroup, group };
};
